// src/pages/Menu/Finance/OutcomeList.jsx
import React, { useEffect, useMemo, useState } from 'react';
import { Plus, Trash2, Edit3, RefreshCcw, Filter, ChevronDown, ChevronRight, X, ArrowLeft, Download } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import { useAuth } from '../../../context/authContext';
import { Banner } from '../../../components/common/Banner';
import ExcelJS from 'exceljs';
import {
  listOutcomes,
  listOutcomeConcepts,
  createOutcome,
  updateOutcome,
  deleteOutcome,
} from '../../../services/financeOutcomesService';
import OutcomeFormModal from './OutcomeFormModal';
import OutcomeConceptsManager from './OutcomeConceptsManager';

const Money = (v) => {
  const num = Number(v) || 0;
  // Mostrar sin redondeo, con todos los decimales que tenga
  const parts = num.toString().split('.');
  const integerPart = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  const decimalPart = parts[1] || '';
  return `$${integerPart}${decimalPart ? ',' + decimalPart : ''}`;
};
const mesLargo = (iso) => (iso ? new Date(iso).toLocaleString('es-CL', { month: 'long' }) : '-');
const anio = (iso) => (iso ? String(new Date(iso).getFullYear()) : '-');
const iso = (d) => (d ? new Date(d).toISOString().slice(0, 10) : '-');

const conceptoEtiqueta = (row, concepts = []) => {
  if (!row?.lines || row.lines.length === 0) return 'Egreso';
  const firstLine = row.lines[0];
  if (firstLine?.concept?.name) return firstLine.concept.name;
  if (firstLine?.concept?.code) return firstLine.concept.code;
  // Si no viene el concepto cargado, buscarlo en la lista
  if (firstLine?.conceptId && concepts.length > 0) {
    const found = concepts.find(c => c.id === firstLine.conceptId);
    if (found?.name) return found.name;
    if (found?.code) return found.code;
  }
  return 'Egreso';
};

export default function OutcomeList() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const rut = useMemo(() => localStorage.getItem('rut') || user?.rut || user?.company?.rut || '', [user]);

  const [rows, setRows] = useState([]);
  const [concepts, setConcepts] = useState([]);
  const [loading, setLoading] = useState(true);

  // Filtros
  const [from, setFrom] = useState(() => new Date(new Date().getFullYear(), 0, 1).toISOString().slice(0, 10));
  const [to, setTo] = useState(() => new Date().toISOString().slice(0, 10));
  const [q, setQ] = useState('');
  const [filterMonth, setFilterMonth] = useState('');
  const [filterYear, setFilterYear] = useState('');
  const [filterCompany, setFilterCompany] = useState('');
  const [filterConcept, setFilterConcept] = useState('');

  // UI state
  const [openForm, setOpenForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [openConcepts, setOpenConcepts] = useState(false);
  const [openRow, setOpenRow] = useState({}); // id:boolean

  const calcTotal = (r) => (r?.lines || []).reduce((s, l) => s + (Number(l?.amount) || 0), 0);

  const loadAll = async () => {
    try {
      setLoading(true);
      const [list, cpts] = await Promise.all([
        listOutcomes({ rut, from, to }),
        listOutcomeConcepts(rut),
      ]);

      const normalized = (list || []).map((r) => {
        // Asegurar que los conceptos se carguen correctamente
        if (r.lines && r.lines.length > 0) {
          r.lines = r.lines.map((line) => {
            // Si no viene el concepto cargado pero hay conceptId, buscarlo en la lista
            if (!line.concept && line.conceptId && cpts && cpts.length > 0) {
              const foundConcept = cpts.find(c => c.id === line.conceptId);
              if (foundConcept) {
                return { ...line, concept: foundConcept };
              }
            }
            return line;
          });
        }
        return {
          ...r,
          total: calcTotal(r),
          fechaRegistro: r.date || r.createdAt, // preferimos date (fecha del egreso) sobre createdAt
          mes: mesLargo(r.date),
          anio: anio(r.date),
          etiqueta: conceptoEtiqueta(r, cpts),
          pagadoA: r.companyName || '-',
          razonSocial: r.legalName || r.companyName || '-',
        };
      });

      // Ordenar por fecha del egreso (date) ascendente, luego por ID ascendente
      const sorted = normalized.sort((a, b) => {
        const dateA = a.date ? new Date(a.date).getTime() : 0;
        const dateB = b.date ? new Date(b.date).getTime() : 0;
        if (dateA !== dateB) return dateA - dateB; // Más antiguo primero
        return (a.id || 0) - (b.id || 0); // Si misma fecha, por ID ascendente
      });
      setRows(sorted);
      setConcepts(cpts || []);
    } catch (e) {
      Swal.fire('Error', e.message || 'No se pudieron cargar los egresos', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (rut) loadAll(); }, [rut, from, to]);

  // Extraer opciones únicas para los filtros
  const filterOptions = useMemo(() => {
    const months = new Set();
    const years = new Set();
    const companies = new Set();
    const concepts = new Set();

    rows.forEach((r) => {
      if (r.mes) months.add(r.mes);
      if (r.anio) years.add(r.anio);
      if (r.pagadoA && r.pagadoA !== '-') companies.add(r.pagadoA);
      (r.lines || []).forEach((l) => {
        const conceptName = l?.concept?.name || l?.concept?.code;
        if (conceptName) concepts.add(conceptName);
      });
    });

    return {
      months: Array.from(months).sort(),
      years: Array.from(years).sort((a, b) => Number(b) - Number(a)),
      companies: Array.from(companies).sort(),
      concepts: Array.from(concepts).sort(),
    };
  }, [rows]);

  const filtered = useMemo(() => {
    let result = rows;

    // Filtro de búsqueda general
    if (q.trim()) {
      const n = q.trim().toLowerCase();
      result = result.filter((r) =>
        String(r.descripcion || r.description || '').toLowerCase().includes(n) ||
        String(r.pagadoA || '').toLowerCase().includes(n) ||
        String(r.razonSocial || '').toLowerCase().includes(n) ||
        String(r.rut || '').toLowerCase().includes(n) ||
        String(r.id || '').includes(n) ||
        String(r.etiqueta || '').toLowerCase().includes(n) ||
        String(r.mes || '').toLowerCase().includes(n) ||
        String(r.anio || '').includes(n)
      );
    }

    // Filtro por mes
    if (filterMonth) {
      result = result.filter((r) => r.mes === filterMonth);
    }

    // Filtro por año
    if (filterYear) {
      result = result.filter((r) => r.anio === filterYear);
    }

    // Filtro por empresa
    if (filterCompany) {
      result = result.filter((r) => r.pagadoA === filterCompany);
    }

    // Filtro por concepto
    if (filterConcept) {
      result = result.filter((r) =>
        (r.lines || []).some((l) => {
          const conceptName = l?.concept?.name || l?.concept?.code;
          return conceptName === filterConcept;
        })
      );
    }

    return result;
  }, [rows, q, filterMonth, filterYear, filterCompany, filterConcept]);

  const startNew = () => { setEditing(null); setOpenForm(true); };
  const startEdit = (row) => { setEditing(row); setOpenForm(true); };

  const handleSave = async (payload) => {
    try {
      if (editing?.id) {
        const saved = await updateOutcome(editing.id, payload);
        const patched = {
          ...saved,
          total: calcTotal(saved),
          fechaRegistro: saved.date || saved.createdAt,
          mes: mesLargo(saved.date),
          anio: anio(saved.date),
          etiqueta: conceptoEtiqueta(saved, concepts),
          pagadoA: saved.companyName || '-',
          razonSocial: saved.legalName || saved.companyName || '-',
        };
        setRows((prev) => {
          const updated = prev.map((r) => (r.id === patched.id ? patched : r));
          // Reordenar después de actualizar
          return updated.sort((a, b) => {
            const dateA = a.date ? new Date(a.date).getTime() : 0;
            const dateB = b.date ? new Date(b.date).getTime() : 0;
            if (dateA !== dateB) return dateA - dateB;
            return (a.id || 0) - (b.id || 0);
          });
        });
        Swal.fire('OK', 'Registro actualizado', 'success');
      } else {
        const saved = await createOutcome(payload);
        const patched = {
          ...saved,
          total: calcTotal(saved),
          fechaRegistro: saved.date || saved.createdAt,
          mes: mesLargo(saved.date),
          anio: anio(saved.date),
          etiqueta: conceptoEtiqueta(saved, concepts),
          pagadoA: saved.companyName || '-',
          razonSocial: saved.legalName || saved.companyName || '-',
        };
        setRows((prev) => {
          const updated = [...prev, patched];
          // Reordenar después de agregar
          return updated.sort((a, b) => {
            const dateA = a.date ? new Date(a.date).getTime() : 0;
            const dateB = b.date ? new Date(b.date).getTime() : 0;
            if (dateA !== dateB) return dateA - dateB;
            return (a.id || 0) - (b.id || 0);
          });
        });
        Swal.fire('OK', 'Registro creado', 'success');
      }
      setOpenForm(false);
    } catch (e) {
      Swal.fire('Error', e.message || 'No se pudo guardar', 'error');
    }
  };

  const handleDelete = async (row) => {
    const c = await Swal.fire({
      icon: 'warning',
      title: '¿Eliminar registro?',
      text: `Se eliminará el egreso #${row.id}`,
      showCancelButton: true,
      confirmButtonText: 'Eliminar',
      cancelButtonText: 'Cancelar',
    });
    if (!c.isConfirmed) return;
    try {
      await deleteOutcome(row.id);
      setRows((prev) => prev.filter((r) => r.id !== row.id));
      Swal.fire('OK', 'Registro eliminado', 'success');
    } catch (e) {
      Swal.fire('Error', e.message || 'No se pudo eliminar', 'error');
    }
  };

  const handleExportExcel = async () => {
    try {
      if (filtered.length === 0) {
        Swal.fire('Info', 'No hay datos para exportar', 'info');
        return;
      }

      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet('Egresos');

      // Estilos
      const headerStyle = {
        font: { bold: true, size: 12, color: { argb: 'FFFFFFFF' } },
        fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF0488AF' } },
        border: {
          top: { style: 'medium', color: { argb: 'FF000000' } },
          bottom: { style: 'medium', color: { argb: 'FF000000' } },
          left: { style: 'medium', color: { argb: 'FF000000' } },
          right: { style: 'medium', color: { argb: 'FF000000' } },
        },
        alignment: { vertical: 'middle', horizontal: 'center' },
      };

      const greenRowStyle = {
        fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFE2EFDA' } },
      };

      // Columnas principales
      worksheet.columns = [
        { header: 'ID', key: 'id', width: 8 },
        { header: 'Fecha', key: 'fecha', width: 12 },
        { header: 'Pagado a', key: 'pagadoA', width: 30 },
        { header: 'Razón Social', key: 'razonSocial', width: 30 },
        { header: 'Descripción', key: 'descripcion', width: 40 },
        { header: 'Total', key: 'total', width: 15, style: { numFmt: '"$"#,##0' } },
      ];

      // Aplicar estilo a la cabecera
      worksheet.getRow(1).eachCell((cell) => { cell.style = headerStyle; });

      // Agregar datos principales
      filtered.forEach((row, index) => {
        const fechaFormateada = row.fechaRegistro 
          ? new Date(row.fechaRegistro).toLocaleDateString('es-CL', { day: '2-digit', month: '2-digit', year: 'numeric' })
          : '-';
        
        worksheet.addRow({
          id: row.id || '',
          fecha: fechaFormateada,
          pagadoA: row.pagadoA || '-',
          razonSocial: row.razonSocial || '-',
          descripcion: row.description || '-',
          total: Number(row.total) || 0,
        });

        // Aplicar estilo zebra
        const currentRow = worksheet.getRow(index + 2);
        if ((index + 1) % 2 === 0) {
          currentRow.eachCell((cell) => {
            cell.style = { ...cell.style, ...greenRowStyle };
          });
        }

        // Agregar desglose de líneas si existen
        if (row.lines && row.lines.length > 0) {
          row.lines.forEach((line) => {
            let conceptoNombre = 'Sin concepto';
            const concept = line?.concept;
            if (concept) {
              if (concept.name && concept.name.trim()) conceptoNombre = concept.name;
              else if (concept.code && concept.code.trim()) conceptoNombre = concept.code;
            } else if (line?.conceptId && concepts && concepts.length > 0) {
              const foundConcept = concepts.find(c => c.id === line.conceptId);
              if (foundConcept) {
                if (foundConcept.name && foundConcept.name.trim()) conceptoNombre = foundConcept.name;
                else if (foundConcept.code && foundConcept.code.trim()) conceptoNombre = foundConcept.code;
              }
            }

            const detalle = line?.activity && line.activity.trim() ? line.activity : '-';
            const monto = Number(line?.amount) || 0;

            worksheet.addRow({
              id: '',
              fecha: '',
              pagadoA: '',
              razonSocial: '',
              descripcion: `  → ${conceptoNombre}${detalle !== '-' ? ` (${detalle})` : ''}`,
              total: monto,
            });
          });
        }
      });

      // Formato de totales
      worksheet.eachRow((row, rowNumber) => {
        if (rowNumber > 1) {
          const totalCell = row.getCell(6);
          if (totalCell.value && typeof totalCell.value === 'number') {
            totalCell.numFmt = '"$"#,##0';
          }
        }
      });

      // Autofiltro
      worksheet.autoFilter = {
        from: { row: 1, column: 1 },
        to: { row: 1, column: worksheet.columnCount },
      };

      // Descargar
      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const fechaDesde = from ? new Date(from).toISOString().split('T')[0] : 'todo';
      const fechaHasta = to ? new Date(to).toISOString().split('T')[0] : 'todo';
      a.download = `Egresos_${fechaDesde}_${fechaHasta}_${new Date().toISOString().split('T')[0]}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
      
      Swal.fire('Éxito', 'Archivo Excel generado correctamente', 'success');
    } catch (error) {
      console.error('Error al exportar a Excel:', error);
      Swal.fire('Error', 'Error al exportar los datos a Excel. Por favor, intenta nuevamente.', 'error');
    }
  };

  return (
    <div className="w-full">
      {/* Banner arriba */}
      <div className="relative">
        <Banner 
          title="Egresos" 
          message="Registro y gestión de egresos por concepto, actividad y cuenta contable"
          className="p-4"
        />
        {/* Botón de volver con animación hover */}
        <button
          onClick={() => navigate('/panel/finanzas')}
          className="absolute top-4 left-4 sm:top-6 sm:left-6 inline-flex items-center gap-1.5 rounded-xl px-3 py-1.5 bg-white/10 hover:bg-white/15 ring-1 ring-white/20 transition-all duration-300 text-sm text-white z-10 group"
          title="Volver"
        >
          <ArrowLeft size={16} className="transition-transform duration-300 group-hover:translate-x-[-4px] group-hover:scale-110" /> 
          <span className="transition-all duration-300 group-hover:font-semibold">Volver</span>
        </button>
      </div>
      
      {/* Header / Filtros */}
      <div className="flex flex-col gap-3 mb-4">
        {/* Primera fila: Filtros principales */}
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
          <div className="flex flex-wrap items-end gap-3">
            <div>
              <label className="text-xs text-gray-600">Desde</label>
              <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="block rounded border px-3 py-1.5 text-sm" />
            </div>
            <div>
              <label className="text-xs text-gray-600">Hasta</label>
              <input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="block rounded border px-3 py-1.5 text-sm" />
            </div>
            <div>
              <label className="text-xs text-gray-600">Buscar</label>
              <input
                type="text"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Empresa / Razón social / RUT / detalle"
                className="block rounded border px-3 py-1.5 text-sm"
              />
            </div>
          </div>

          <div className="flex flex-wrap items-end gap-3">
            <button
              onClick={loadAll}
              className="inline-flex items-center gap-2 px-3 py-2 rounded border hover:bg-gray-50"
              title="Refrescar"
            >
              <RefreshCcw size={16} /> <span className="hidden md:inline">Refrescar</span>
            </button>

            <button
              onClick={handleExportExcel}
              className="inline-flex items-center gap-2 px-4 h-11 rounded-xl bg-green-600 text-white hover:bg-green-700 transition-all duration-200"
              title="Exportar a Excel"
              disabled={filtered.length === 0}
            >
              <Download size={16} /> <span className="hidden md:inline">Exportar Excel</span>
            </button>

            <button
              onClick={() => setOpenConcepts(true)}
              className="inline-flex items-center gap-2 px-4 h-11 rounded-xl bg-[#0488AF] text-white hover:bg-[#037a98] transition-all duration-200"
              title="Conceptos"
            >
              <Filter size={16} /> <span className="hidden md:inline">Conceptos</span>
            </button>

            <button
              onClick={() => { setEditing(null); setOpenForm(true); }}
              className="inline-flex items-center gap-2 px-4 h-11 rounded-xl bg-[#0488AF] text-white hover:bg-[#037a98] transition-all duration-200 relative z-10"
            >
              <Plus size={16} /> Nuevo egreso
            </button>
          </div>
        </div>

        {/* Segunda fila: Filtros adicionales */}
        <div className="flex flex-wrap items-end gap-3 p-3 bg-gray-50 rounded-lg border border-gray-200">
          <div>
            <label className="text-xs text-gray-600 font-medium">Mes</label>
            <select
              value={filterMonth}
              onChange={(e) => setFilterMonth(e.target.value)}
              className="block rounded border px-3 py-1.5 text-sm min-w-[140px] bg-white"
            >
              <option value="">Todos los meses</option>
              {filterOptions.months.map((month) => (
                <option key={month} value={month}>
                  {month.charAt(0).toUpperCase() + month.slice(1)}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="text-xs text-gray-600 font-medium">Año</label>
            <select
              value={filterYear}
              onChange={(e) => setFilterYear(e.target.value)}
              className="block rounded border px-3 py-1.5 text-sm min-w-[120px] bg-white"
            >
              <option value="">Todos los años</option>
              {filterOptions.years.map((year) => (
                <option key={year} value={year}>
                  {year}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="text-xs text-gray-600 font-medium">Empresa</label>
            <select
              value={filterCompany}
              onChange={(e) => setFilterCompany(e.target.value)}
              className="block rounded border px-3 py-1.5 text-sm min-w-[200px] bg-white"
            >
              <option value="">Todas las empresas</option>
              {filterOptions.companies.map((company) => (
                <option key={company} value={company}>
                  {company}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="text-xs text-gray-600 font-medium">Concepto</label>
            <select
              value={filterConcept}
              onChange={(e) => setFilterConcept(e.target.value)}
              className="block rounded border px-3 py-1.5 text-sm min-w-[180px] bg-white"
            >
              <option value="">Todos los conceptos</option>
              {filterOptions.concepts.map((concept) => (
                <option key={concept} value={concept}>
                  {concept}
                </option>
              ))}
            </select>
          </div>

          {(filterMonth || filterYear || filterCompany || filterConcept) && (
            <button
              onClick={() => {
                setFilterMonth('');
                setFilterYear('');
                setFilterCompany('');
                setFilterConcept('');
              }}
              className="inline-flex items-center gap-1 px-3 py-1.5 rounded border border-red-300 text-red-600 hover:bg-red-50 text-sm"
              title="Limpiar filtros"
            >
              <X size={14} /> Limpiar filtros
            </button>
          )}
        </div>
      </div>

      {/* Tabla principal - Libro Diario */}
      <div className="w-full overflow-auto rounded border bg-white">
        <table className="min-w-[900px] w-full text-xs">
          <thead className="bg-gray-100 sticky top-0 z-10 border-b-2 border-gray-300">
            <tr className="text-left">
              <th className="px-2 py-2 font-semibold text-gray-700 border-r">Fecha</th>
              <th className="px-2 py-2 font-semibold text-gray-700 border-r">Mes</th>
              <th className="px-2 py-2 font-semibold text-gray-700 border-r">N° Reg.</th>
              <th className="px-2 py-2 font-semibold text-gray-700 border-r">Empresa</th>
              <th className="px-2 py-2 font-semibold text-gray-700 border-r">Detalle</th>
              <th className="px-2 py-2 font-semibold text-gray-700 border-r">Conceptos</th>
              <th className="px-2 py-2 font-semibold text-gray-700 border-r text-right">Total</th>
              <th className="px-2 py-2 font-semibold text-gray-700 text-center">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={8} className="px-2 py-3 text-gray-600 text-center text-xs">Cargando...</td></tr>
            ) : filtered.length === 0 ? (
              <tr><td colSpan={8} className="px-2 py-3 text-gray-600 text-center text-xs">Sin resultados</td></tr>
            ) : (
              filtered.map((row) => {
                const abierto = !!openRow[row.id];
                const fechaFormateada = row.fechaRegistro ? new Date(row.fechaRegistro).toLocaleDateString('es-CL', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '-';
                const mesAnio = `${row.mes ? row.mes.charAt(0).toUpperCase() + row.mes.slice(1) : ''} ${row.anio || ''}`.trim();
                const conceptosList = (row.lines || []).map(l => {
                  // Intentar obtener el concepto de diferentes formas
                  const concept = l?.concept;
                  if (concept) {
                    if (concept.name && concept.name.trim()) return concept.name;
                    if (concept.code && concept.code.trim()) return concept.code;
                  }
                  // Si hay conceptId pero no concepto, buscar en la lista de conceptos
                  if (l?.conceptId && concepts && concepts.length > 0) {
                    const foundConcept = concepts.find(c => c.id === l.conceptId);
                    if (foundConcept) {
                      if (foundConcept.name && foundConcept.name.trim()) return foundConcept.name;
                      if (foundConcept.code && foundConcept.code.trim()) return foundConcept.code;
                    }
                  }
                  // Si hay conceptId pero no se encontró el concepto
                  if (l?.conceptId) return `Concepto ID: ${l.conceptId}`;
                  return 'Sin concepto';
                }).join(', ');
                
                return (
                  <React.Fragment key={row.id}>
                    <tr className={`border-b hover:bg-gray-50 ${abierto ? 'bg-blue-50' : ''}`}>
                      <td className="px-2 py-2 border-r">
                        <button
                          onClick={() => setOpenRow((s) => ({ ...s, [row.id]: !abierto }))}
                          className="inline-flex items-center gap-1 text-gray-800 hover:text-blue-600 font-medium"
                          title={abierto ? 'Ocultar desglose' : 'Ver desglose'}
                        >
                          {abierto ? <ChevronDown size={14}/> : <ChevronRight size={14}/>}
                          {fechaFormateada}
                        </button>
                      </td>
                      <td className="px-2 py-2 border-r capitalize text-gray-700">{mesAnio}</td>
                      <td className="px-2 py-2 border-r font-mono text-gray-600 text-xs">{row.id}</td>
                      <td className="px-2 py-2 border-r">
                        <div className="max-w-xs">
                          <div className="font-medium text-gray-900 text-xs">{row.pagadoA || '-'}</div>
                        </div>
                      </td>
                      <td className="px-2 py-2 border-r">
                        <div className="max-w-xs">
                          <div className="text-xs text-gray-700">
                            {row.description || '-'}
                          </div>
                        </div>
                      </td>
                      <td className="px-2 py-2 border-r">
                        <div className="max-w-xs">
                          <div className="text-gray-700 text-xs">{conceptosList || '-'}</div>
                          {(row.lines || []).length > 1 && (
                            <div className="text-xs text-gray-400 mt-0.5">
                              {row.lines.length} concepto{(row.lines.length > 1 ? 's' : '')}
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="px-2 py-2 border-r text-right font-semibold text-red-700 text-xs">{Money(row.total)}</td>
                      <td className="px-2 py-2">
                        <div className="flex items-center justify-center gap-1">
                          <button
                            onClick={() => startEdit(row)}
                            className="p-1 rounded border hover:bg-blue-50 text-blue-600"
                            title="Editar"
                          >
                            <Edit3 size={12} />
                          </button>
                          <button
                            onClick={() => handleDelete(row)}
                            className="p-1 rounded border hover:bg-red-50 text-red-600"
                            title="Eliminar"
                          >
                            <Trash2 size={12} />
                          </button>
                        </div>
                      </td>
                    </tr>

                    {/* Desglose contable expandido (libro diario) */}
                    {abierto && (
                      <tr className="bg-gray-50">
                        <td colSpan={8} className="px-4 py-4">
                          <div className="bg-white rounded-lg border-2 border-gray-200 shadow-sm">
                            <div className="px-4 py-3 border-b-2 border-gray-200 bg-gray-50">
                              <div className="flex items-center justify-between">
                                <div>
                                  <h3 className="font-semibold text-gray-800">Libro Diario — Registro #{row.id}</h3>
                                  <p className="text-xs text-gray-500 mt-0.5">
                                    {fechaFormateada} • {mesAnio} • {row.pagadoA || 'Sin empresa'}
                                  </p>
                                </div>
                                <button
                                  onClick={() => startEdit(row)}
                                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded border hover:bg-blue-50 text-blue-600 text-sm"
                                >
                                  <Edit3 size={14} /> Editar
                                </button>
                              </div>
                            </div>
                            {/* Información general del egreso */}
                            <div className="px-4 py-3 border-b border-gray-200 bg-gray-50">
                              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                                <div>
                                  <div className="text-xs text-gray-500 mb-1">Descripción</div>
                                  <div className="font-medium text-gray-800">{row.description || '-'}</div>
                                </div>
                                <div>
                                  <div className="text-xs text-gray-500 mb-1">Pagado a</div>
                                  <div className="font-medium text-gray-800">{row.pagadoA || '-'}</div>
                                </div>
                                <div>
                                  <div className="text-xs text-gray-500 mb-1">Fecha</div>
                                  <div className="font-medium text-gray-800">{fechaFormateada}</div>
                                </div>
                                <div>
                                  <div className="text-xs text-gray-500 mb-1">Total</div>
                                  <div className="font-bold text-red-700">{Money(row.total)}</div>
                                </div>
                              </div>
                            </div>
                            
                            <div className="p-4 overflow-auto">
                              <table className="min-w-[900px] w-full text-sm">
                                <thead className="bg-gray-100">
                                  <tr className="text-left">
                                    <th className="px-3 py-2 border-r font-semibold text-gray-700">Concepto</th>
                                    <th className="px-3 py-2 border-r font-semibold text-gray-700">Detalle</th>
                                    <th className="px-3 py-2 font-semibold text-gray-700 text-right">Monto</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {(row.lines || []).map((l, i) => {
                                    // Intentar obtener el concepto de diferentes formas
                                    let conceptName = '(s/concepto)';
                                    const concept = l?.concept;
                                    if (concept) {
                                      if (concept.name && concept.name.trim()) {
                                        conceptName = concept.name;
                                      } else if (concept.code && concept.code.trim()) {
                                        conceptName = concept.code;
                                      }
                                    }
                                    // Si hay conceptId pero no concepto, buscar en la lista de conceptos
                                    if (conceptName === '(s/concepto)' && l?.conceptId && concepts && concepts.length > 0) {
                                      const foundConcept = concepts.find(c => c.id === l.conceptId);
                                      if (foundConcept) {
                                        if (foundConcept.name && foundConcept.name.trim()) {
                                          conceptName = foundConcept.name;
                                        } else if (foundConcept.code && foundConcept.code.trim()) {
                                          conceptName = foundConcept.code;
                                        } else {
                                          conceptName = `Concepto ID: ${l.conceptId}`;
                                        }
                                      } else {
                                        conceptName = `Concepto ID: ${l.conceptId}`;
                                      }
                                    }
                                    
                                    // Detalle adicional (período/activity)
                                    let detalle = '-';
                                    if (l?.activity && l.activity.trim()) {
                                      detalle = l.activity;
                                    }
                                    
                                    return (
                                      <tr key={i} className="border-b hover:bg-gray-50">
                                        <td className="px-3 py-2 border-r font-medium">{conceptName}</td>
                                        <td className="px-3 py-2 border-r text-gray-600 text-xs">
                                          {detalle !== '-' ? (
                                            <span className="font-medium text-blue-700">{detalle}</span>
                                          ) : (
                                            <span className="text-gray-400">{detalle}</span>
                                          )}
                                        </td>
                                        <td className="px-3 py-2 text-right font-semibold text-red-700">{Money(l?.amount)}</td>
                                      </tr>
                                    );
                                  })}
                                  <tr className="border-t-2 border-gray-300 bg-gray-100 font-bold">
                                    <td className="px-3 py-2.5 border-r">TOTAL</td>
                                    <td className="px-3 py-2.5 border-r"></td>
                                    <td className="px-3 py-2.5 text-right text-red-700">{Money(row.total)}</td>
                                  </tr>
                                </tbody>
                              </table>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })
            )}
          </tbody>
          {!loading && filtered.length > 0 && (
            <tfoot className="bg-gray-100 border-t-2 border-gray-300 sticky bottom-0">
              <tr>
                <td colSpan={6} className="px-2 py-2 font-bold text-gray-800 text-right text-xs">
                  TOTAL GENERAL:
                </td>
                <td className="px-2 py-2 text-right font-bold text-sm text-red-700 border-r">
                  {Money(filtered.reduce((sum, r) => sum + (r.total || 0), 0))}
                </td>
                <td className="px-2 py-2"></td>
              </tr>
              <tr className="bg-gray-50">
                <td colSpan={6} className="px-2 py-1.5 text-xs text-gray-600 text-right">
                  Registros mostrados: {filtered.length}
                </td>
                <td className="px-2 py-1.5 border-r"></td>
                <td className="px-2 py-1.5"></td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>

      {/* Modales */}
      <OutcomeFormModal
        open={openForm}
        onClose={() => setOpenForm(false)}
        rut={rut}
        concepts={concepts}
        initialData={editing}
        onSave={handleSave}
      />

      <OutcomeConceptsManager
        open={openConcepts}
        onClose={() => setOpenConcepts(false)}
        rut={rut}
      />
    </div>
  );
}

